import { apiClient } from '@/lib/api-client';
import type {
  ApiTokenCreated,
  ApiTokenInfo,
  AuthUser,
  LoginResponse,
  SessionInfo,
  TwoFactorSetup,
} from '@/types/api';

type Message = { message: string };
type AuthenticatedLogin = Extract<LoginResponse, { two_factor_required: false }>;

/** Typed wrappers over the Identity module's REST endpoints. */
export const authApi = {
  // Public
  /**
   * `company` is optional and sent as an empty string when omitted — plenty of customers are
   * individuals, and a required company field has them type a placeholder that then appears on
   * their invoices.
   */
  register: (
    email: string,
    password: string,
    firstName: string,
    lastName: string,
    company: string,
  ) =>
    apiClient.post<AuthUser>(
      '/auth/register',
      { email, password, first_name: firstName, last_name: lastName, company },
      { skipAuth: true },
    ),
  verifyEmail: (token: string) =>
    apiClient.post<AuthUser>('/auth/verify-email', { token }, { skipAuth: true }),
  resendVerification: (email: string) =>
    apiClient.post<Message>('/auth/resend-verification', { email }, { skipAuth: true }),
  login: (email: string, password: string) =>
    apiClient.post<LoginResponse>('/auth/login', { email, password }, { skipAuth: true }),

  /**
   * Where to send somebody who chose "Continue with Google".
   *
   * `available` is false when the installation has no Google credentials, which is how the button
   * knows to stay hidden rather than appear and fail on click.
   */
  googleStart: (redirectUri: string) =>
    apiClient.post<{ available: boolean; url: string; state: string }>(
      '/auth/google/start',
      { redirect_uri: redirectUri },
      { skipAuth: true },
    ),

  /** Trades the code Google sent back for a session here. */
  googleCallback: (code: string, redirectUri: string) =>
    apiClient.post<LoginResponse>(
      '/auth/google/callback',
      { code, redirect_uri: redirectUri },
      { skipAuth: true },
    ),
  completeTwoFactor: (challenge_token: string, code: string) =>
    apiClient.post<AuthenticatedLogin>('/auth/login/2fa', { challenge_token, code }, { skipAuth: true }),
  forgotPassword: (email: string) =>
    apiClient.post<Message>('/auth/forgot-password', { email }, { skipAuth: true }),
  resetPassword: (token: string, new_password: string) =>
    apiClient.post<Message>('/auth/reset-password', { token, new_password }, { skipAuth: true }),
  logout: (refresh_token: string) =>
    apiClient.post<void>('/auth/logout', { refresh_token }, { skipAuth: true }),

  // Authenticated
  me: () => apiClient.get<AuthUser>('/account/me'),
  changePassword: (current_password: string, new_password: string) =>
    apiClient.post<Message>('/account/password', { current_password, new_password }),
  enableTwoFactor: () => apiClient.post<TwoFactorSetup>('/account/2fa/enable'),
  confirmTwoFactor: (code: string) => apiClient.post<Message>('/account/2fa/confirm', { code }),
  disableTwoFactor: (password: string) =>
    apiClient.post<Message>('/account/2fa/disable', { password }),

  listSessions: () => apiClient.get<SessionInfo[]>('/account/sessions'),
  revokeSession: (id: string) => apiClient.delete<void>(`/account/sessions/${id}`),
  revokeOtherSessions: () => apiClient.post<Message>('/account/sessions/revoke-others'),

  listApiTokens: () => apiClient.get<ApiTokenInfo[]>('/account/api-tokens'),
  createApiToken: (name: string, abilities: string[], expires_in_days: number | null) =>
    apiClient.post<ApiTokenCreated>('/account/api-tokens', { name, abilities, expires_in_days }),
  revokeApiToken: (id: string) => apiClient.delete<void>(`/account/api-tokens/${id}`),
};
